<template>
  <MGuidedCheckoutStep
    :step-number="stepNumber"
    title="Wähle die Dämmung."
    :description="stepDescription"
  >
    <div class="m-gc-step-underlay__help">
      <button
        type="button"
        class="m-gc-step-underlay__help-button"
        @click="openHelpModal"
      >
        Brauchst du Hilfe bei der Auswahl einer Dämmung?
      </button>
    </div>

    <div class="m-gc-step-underlay__items product-boxes-items">
      <button
        v-if="packagePreset === 'einzelprodukt'"
        type="button"
        class="m-gc-step-underlay__item"
        :class="{ 'm-gc-step-underlay__item--active': !selectedLinkId }"
        @click="clearSelection"
      >
        <div
          class="m-gc-step-underlay__item-thumb m-gc-step-underlay__item-thumb--empty"
          aria-hidden="true"
        />
        <div class="m-gc-step-underlay__item-content">
          <strong>Keine Dämmung</strong>
          <AProductBasePrice
            :regular="0"
            :factor="1"
            unit="m²"
          />
        </div>
      </button>

      <button
        v-for="link in visibleLinks"
        :key="link.id"
        type="button"
        class="m-gc-step-underlay__item"
        :class="{ 'm-gc-step-underlay__item--active': selectedLinkId === link.id }"
        @click="selectLink(link)"
      >
        <SfImage
          v-if="link.product"
          :src="getThumbnailForProduct(link.product)"
          :alt="link.product.name"
          width="72"
          height="72"
        />
        <div class="m-gc-step-underlay__item-content">
          <strong>{{ link.product && link.product.name }}</strong>
          <AProductBasePrice
            v-if="link.product"
            :regular="link.price < link.product.final_price_incl_tax ? link.product.final_price_incl_tax : link.price"
            :special="link.price < link.product.final_price_incl_tax ? link.price : null"
            :factor="link.product.baseprice_product_amount"
            :unit="link.product.baseprice_product_unit"
          />
        </div>
      </button>
    </div>
  </MGuidedCheckoutStep>
</template>

<script>
import { mapActions } from 'vuex'
import { SfImage } from '@storefront-ui/vue'
import { getThumbnailForProduct } from '@vue-storefront/core/modules/cart/helpers'
import MGuidedCheckoutStep from 'theme/components/molecules/guided-checkout/m-guided-checkout-step'
import AProductBasePrice from 'theme/components/atoms/a-product-base-price'
import { ModalList } from 'theme/store/ui/modals'
import {
  clearBundleOptionSelection,
  filterBundleLinksForPreset,
  getUnderlayBundleOption,
  selectBundleOptionLink
} from 'theme/helpers/guided-checkout'
import { getSelectedBundleOptionValue } from 'theme/helpers/bundle'

export default {
  name: 'MGcStepUnderlay',
  components: {
    MGuidedCheckoutStep,
    SfImage,
    AProductBasePrice
  },
  props: {
    stepNumber: {
      type: [Number, String],
      default: 4
    },
    product: {
      type: Object,
      required: true
    },
    bundleOptionsComponent: {
      type: Object,
      default: null
    },
    packagePreset: {
      type: String,
      default: 'standard'
    }
  },
  computed: {
    underlayOption () {
      return getUnderlayBundleOption(this.product)
    },
    visibleLinks () {
      if (!this.underlayOption) return []
      return filterBundleLinksForPreset(this.underlayOption.product_links || [], this.packagePreset)
    },
    stepDescription () {
      if (this.packagePreset === 'einzelprodukt') {
        return 'Beim Einzelboden kannst du eine Dämmung frei wählen oder darauf verzichten.'
      }
      if (this.packagePreset === 'premium') {
        return 'Im Premium Set sind nur Aufpreis-Dämmungen wählbar. Eine passende Auswahl ist vorausgewählt.'
      }
      return 'Im Standard Set sind nur die kostenlosen Standard-Dämmungen wählbar. Eine passende Auswahl ist vorausgewählt.'
    },
    selectedLinkId () {
      if (!this.underlayOption) return null
      const selected = getSelectedBundleOptionValue(this.product, this.underlayOption)
      return selected ? selected.id : null
    }
  },
  methods: {
    ...mapActions('ui', {
      openModal: 'openModal'
    }),
    getThumbnailForProduct,
    openHelpModal () {
      this.openModal({ name: ModalList.UnderlayHelp })
    },
    selectLink (link) {
      selectBundleOptionLink(this.bundleOptionsComponent, this.underlayOption, link)
      this.$emit('changed')
    },
    clearSelection () {
      clearBundleOptionSelection(this.bundleOptionsComponent, this.underlayOption)
      this.$emit('changed')
    }
  }
}
</script>

<style lang="scss" scoped>
.m-gc-step-underlay {
  &__help {
    margin-bottom: 1rem;
  }

  &__help-button {
    display: inline;
    padding: 0;
    height: auto;
    min-height: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    color: var(--c-primary);
    font-weight: 700;
    text-decoration: underline;
    cursor: pointer;
    white-space: normal;
  }

  &__items {
    display: grid;
    gap: 0.75rem;
  }

  &__item {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    width: 100%;
    height: auto;
    min-height: 0;
    padding: 0.75rem;
    border: 1px solid var(--c-light);
    border-radius: 4px;
    background: var(--c-white);
    text-align: left;
    cursor: pointer;
    white-space: normal;
    overflow: hidden;
    transition: var(--gc-transition);

    &--active {
      border-color: var(--c-primary);
      box-shadow: 0 0 0 1px var(--c-primary);
    }

    ::v-deep .sf-image,
    .m-gc-step-underlay__item-thumb {
      flex: 0 0 72px;
      width: 72px;
      height: 72px;
      max-width: 72px;
      overflow: hidden;
      border-radius: 4px;
      background: var(--c-light);

      img {
        width: 72px;
        height: 72px;
        object-fit: cover;
      }
    }

    .m-gc-step-underlay__item-thumb--empty {
      box-shadow: inset 0 0 0 1px var(--c-light);
    }

    strong {
      display: block;
      margin-bottom: 0.35rem;
      line-height: 1.3;
      font-weight: 700;
      white-space: normal;
      overflow-wrap: anywhere;
    }
  }

  &__item-content {
    flex: 1;
    min-width: 0;
  }
}
</style>
